import * as React from 'react';

import { Video } from '@notino/shared/definitions/types';

import { IImageProperties } from '@helpers/utils';

import { getVideoIframe } from '../../../containers/ProductDetailContainer/utils';

interface UseMediaNavigationParams {
  imageIndex: number;
  images: IImageProperties[];
  thumbImages: IImageProperties[];
  videos?: Video[];
  onClose: () => void;
  onMediaChange?: () => void;
}

interface UseMediaNavigationReturn {
  mediaIndex: number;
  setMediaIndex: React.Dispatch<React.SetStateAction<number>>;
  mediaCount: number;
  isFirstItem: boolean;
  isLastItem: boolean;
  currentImage: IImageProperties | undefined;
  currentVideo: string;
  isLoading: boolean;
  imageRef: React.RefObject<HTMLImageElement>;
  handleImageLoad: () => void;
}

export const useMediaNavigation = ({
  imageIndex,
  images,
  thumbImages,
  videos,
  onClose,
  onMediaChange,
}: UseMediaNavigationParams): UseMediaNavigationReturn => {
  const [mediaIndex, setMediaIndex] = React.useState(imageIndex);
  const [isLoading, setIsLoading] = React.useState(true);
  const imageRef = React.useRef<HTMLImageElement>(null);
  const mediaCount = thumbImages.length;

  const currentImage = images[mediaIndex];

  const handleImageLoad = React.useCallback(() => {
    setIsLoading(false);
  }, []);

  React.useEffect(() => {
    setMediaIndex(imageIndex);
  }, [imageIndex]);

  React.useEffect(() => {
    const alreadyLoaded = imageRef.current?.complete ?? false;
    setIsLoading(!alreadyLoaded);
    onMediaChange?.();
  }, [mediaIndex, onMediaChange]);

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        setMediaIndex((prev) => Math.max(0, prev - 1));
      } else if (event.key === 'ArrowRight') {
        setMediaIndex((prev) =>
          Math.min(mediaCount > 1 ? mediaCount - 1 : 0, prev + 1)
        );
      } else if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [mediaCount, onClose]);

  const getVideo = React.useCallback(
    (index: number): string => {
      const videoIndex = index - images.length;
      const video = videos?.[videoIndex];
      return videoIndex < 0 || !video ? '' : getVideoIframe(video, true);
    },
    [images, videos]
  );

  const currentVideo = getVideo(mediaIndex);
  const isFirstItem = mediaIndex === 0;
  const isLastItem = mediaIndex === mediaCount - 1;

  return {
    mediaIndex,
    setMediaIndex,
    mediaCount,
    isFirstItem,
    isLastItem,
    currentImage,
    currentVideo,
    isLoading,
    imageRef,
    handleImageLoad,
  };
};
